import React, { useState, useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { Option, OptionType } from '../../types'
import Button from '../Button/Button'
import Select from '../Select/Select'
import TextField from '../TextField/TextField'
import config from '../../config'
import './Login.scss'
import { useAvailableLocaleOptions, useI18nSelector, useI18nDispatch, useI18nState } from '../../v3/context/i18n'
import { resetDateFilters, setLocalStorage } from '../../util'
import { i18nActionTypes } from '../../actions'
import { isSessionValid } from '../../util/session'
import { useAuth } from '../../hooks/useAuth'
import { AuthErrorCodes } from 'types/Auth'
import { SessionState } from 'reducers/session'
import { datadogLogs } from '@datadog/browser-logs'

export const Auth0Login = () => {
  const [loggingin, setLoggingIn] = useState(true)
  const [notLinked, setNotLinked] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string>('')
  const [email, setEmail] = useState<string>('')
  const [password, setPassword] = useState('')
  const history = useHistory()
  const { locale } = useI18nState()
  const availableLocales = useAvailableLocaleOptions()
  const i18n = useI18nSelector()
  const i18nDispatch = useI18nDispatch()
  const location = useLocation()

  let { from } = location.state || { from: { pathname: '/' } }
  if (!from || !from.pathname || from.pathname === '/login') {
    from = {
      pathname: '/',
    }
  }

  const { handleRedirectCallback, linkUserToSso } = useAuth()

  const handleLoginResponse = (session: SessionState) => {
    if (!isSessionValid(session)) {
      throw new Error('session data from credentials login was not valid')
    }

    setLocalStorage(config.sessionKey, session)
    window.dispatchEvent(new Event('toggle-theme'))
    // AB#8587 Lead list date filters should be reset on every user session
    resetDateFilters()
    // reloading the window; this allows the app to re-fetch the session from storage and load into desktop or mobile views
    history.replace(from)
  }

  useEffect(() => {
    const handleCallback = async () => {
      try {
        setLoggingIn(true)
        const response = await handleRedirectCallback()

        if (response.errorCode && response.errorCode === AuthErrorCodes.SsoUserNotLinked) {
          setNotLinked(true)
          setEmail(response.email || '')
          setErrorMsg(i18n('auth.sso.link.message'))
          return
        }

        handleLoginResponse(response.sessionData!)
      } catch (error) {
        datadogLogs.logger.error('Auth0Login handleCallback error', { error })
        setErrorMsg(i18n('auth.sso.error'))
      } finally {
        setLoggingIn(false)
      }
    }
    handleCallback()
  }, [])

  const handleLinkClick = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault()
    try {
      const response = await linkUserToSso(email, password)
      if (response.errorCode) {
        setErrorMsg(i18n('auth.sso.link.error'))
        return
      }
      handleLoginResponse(response.sessionData!)
    } catch (error) {
      datadogLogs.logger.error('Auth0Login handleLinkClick error', { error })
      setErrorMsg(i18n('auth.sso.error'))
    }
  }

  const logoClasses = ['logo']

  return (
    <>
      <div className="LoginScreen">
        <div className="Login">
          <form className="form">
            <div className={logoClasses.join(' ')} />
            {errorMsg && (
              <div>
                <div className="error">{errorMsg}</div>
                <div className="center">
                  <Button
                    className="submit"
                    id="submit"
                    variant="contained"
                    color="primary"
                    disableElevation
                    onClick={() =>
                      window.open('https://powerchord.atlassian.net/servicedesk/customer/portal/6/group/9/create/16', '_blank', 'noopener,noreferrer')
                    }
                  >
                    {i18n('auth.sso.cta.supportLink')}
                  </Button>
                </div>
              </div>
            )}
            {loggingin && <h2>Logging In...</h2>}
            {!loggingin && notLinked && (
              <div>
                <TextField
                  label={i18n('auth.emailLabel')}
                  onInput={(value: string) => {
                    setEmail(value)
                  }}
                  type="email"
                  value={email}
                  testID={'login-email'}
                />
                <TextField
                  label={i18n('auth.passwordLabel')}
                  onInput={(value: string) => {
                    setPassword(value)
                  }}
                  type="password"
                  value={password}
                  testID={'login-password'}
                />
                <Select
                  dataCIKey={'locale'}
                  label={i18n('locale.language')}
                  options={availableLocales}
                  value={availableLocales.filter((v: any): boolean => locale === v.value)}
                  onChange={(opts: Option[]) => {
                    const locale = opts[0]
                    if (locale.type !== OptionType.Enum) return
                    i18nDispatch({ type: i18nActionTypes.SET_LOCALE, locale: locale.value })
                  }}
                />
                <div className="buttons">
                  <div className="actions login">
                    <Button
                      className="submit"
                      id="submit"
                      variant="contained"
                      color="primary"
                      onClick={handleLinkClick}
                      disableElevation
                      type="submit"
                    >
                      {i18n('auth.sso.cta.linkAccount')}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </form>
          <div className={'recaptcha-disclaimer'}>
            <p
              dangerouslySetInnerHTML={{
                __html: i18n('auth.recaptcha.disclaimer'),
              }}
            ></p>
          </div>
        </div>
      </div>
    </>
  )
}
